import React, { useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { CollectionEmailTemplate, TONE_LABELS } from '../../config/model';
import {
  CollectionEmailSendStatus,
  CollectionEmailSendSourceFilter,
  CollectionEmailType,
  STATUS_LABELS,
  SOURCE_FILTER_LABELS,
  MODULE_LABELS,
  NO_TONE_FILTER_VALUE,
} from '../model';
import { EmailSendsQueryParams } from '../hooks/useCollectionEmailSends';

export type FilterFields = Omit<EmailSendsQueryParams, 'location' | 'page' | 'per_page' | 'sort'>;

interface Props {
  onChange: (filters: FilterFields) => void;
}

const TONES: CollectionEmailTemplate['tone'][] = ['friendly', 'second_notice', 'urgent'];
const STATUSES: CollectionEmailSendStatus[] = ['queued', 'sent', 'failed'];
const SOURCES: CollectionEmailSendSourceFilter[] = ['automatic', 'manual', 'one_off'];
const MODULES: CollectionEmailType[] = ['collection', 'statement'];

const EMPTY_FILTERS: FilterFields = {
  status: '',
  tone: '',
  send_source: '',
  email_type: '',
  company: '',
  bill_code: '',
};

const EmailHistoryFilters: React.FC<Props> = ({ onChange }) => {
  const [fields, setFields] = useState<FilterFields>(EMPTY_FILTERS);

  const handleChange = (key: keyof FilterFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onChange(fields);
  };

  const handleClear = () => {
    setFields(EMPTY_FILTERS);
    onChange(EMPTY_FILTERS);
  };

  return (
    <Box sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Company"
            value={fields.company ?? ''}
            onChange={(e) => handleChange('company', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Bill Code"
            value={fields.bill_code ?? ''}
            onChange={(e) => handleChange('bill_code', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Module</InputLabel>
            <Select
              value={fields.email_type ?? ''}
              label="Module"
              onChange={(e) => handleChange('email_type', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {MODULES.map((m) => (
                <MenuItem key={m} value={m}>
                  {MODULE_LABELS[m]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Tone</InputLabel>
            <Select value={fields.tone ?? ''} label="Tone" onChange={(e) => handleChange('tone', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value={NO_TONE_FILTER_VALUE}>No Tone</MenuItem>
              {TONES.map((t) => (
                <MenuItem key={t} value={t}>
                  {TONE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={fields.status ?? ''} label="Status" onChange={(e) => handleChange('status', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select
              value={fields.send_source ?? ''}
              label="Source"
              onChange={(e) => handleChange('send_source', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {SOURCES.map((s) => (
                <MenuItem key={s} value={s}>
                  {SOURCE_FILTER_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="outlined" size="small" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailHistoryFilters;
